
import { Component, Vue, Watch } from "vue-property-decorator";
import httpClient from "../../store/httpClient";
import { PressJoinCategory } from "../../dtos/pressJoinCategory";
import { PressJoinParameterDto } from "../../dtos/pressJoinParameterDto";
import rules from "@/store/rules";
import { ArticleDto } from "@/dtos/articleDto";
import ArticleCadPreview from "@/components/ArticleCadPreview.vue";

@Component({
	components: {
		ArticleCadPreview,
	},
})
export default class PressJoinSettings extends Vue {
	private loading: boolean = false;
	private forms: PressJoinParameterDto[] = [];
	private requiredRule = rules.requiredRule;
	private positiveNumberRule = rules.requiredNumberBiggerZeroRule;
	private positionHeader = [
		{ text: "Name (Gruppe)", value: "groupName", sortable: false },
		{ text: "Bild", value: "thumbnail", sortable: false },
		{ text: "Einpressartikel", value: "werkzeug", sortable: false },
		{ text: "Kernlochdurchmesser [mm]", value: "kernlochDurchmesser", sortable: false },
		{ text: "", value: "actions", sortable: false },
	];
	private categories = [
		{
			value: PressJoinCategory.SelfClinchingNuts,
			text: "Einpressmuttern",
		},
		{
			value: PressJoinCategory.SelfClinchingThreadedStud,
			text: "Einpressgewindebolzen",
		},
		{
			value: PressJoinCategory.SelfClinchingThreadedStandoff,
			text: "Einpressgewindebuchse",
		},
	];
	private isEdit: boolean = false;
	private addTemplateDialog: boolean = false;
	private gefilterteArtikel: any = [];
	private articleFilter: string = "";
	private selectedArticle: ArticleDto | null = null;
	private pressJoinGroups = [];
	private selectedPressJoinCategory: number = PressJoinCategory.SelfClinchingNuts;
	private selectedKernlochdurchmesser: number = 0;
	private selectedMinimumADistance: number = 0;
	private selectedMinimumDistance: number = 0;
	private selectedGroup: any | null = null;
	private groupFilter: string = "";
	private newGroupName: string = "";
	private newToolArticleName: string = "";
	private newToolSupplierArticleName: string = "";
	private editFormTemplateId: number = 0;
	private formValid: boolean = true;
	private activeTab: number = PressJoinCategory.SelfClinchingNuts;
	private currentDate: string = Date.now().toString();

	private mounted() {
		this.loadPressJoinForms();
	}

	private get filteredGroups() {
		if (!this.groupFilter || this.groupFilter.length <= 0) return this.pressJoinGroups;
		return this.pressJoinGroups.filter((group: any) =>
			group.name.toLowerCase().includes(this.groupFilter.toLowerCase())
		);
	}

	private get newGroupIsValid(): boolean {
		if (!this.selectedArticle) return false;
		if (!this.selectedGroup && this.newGroupName.length <= 0) return false;
		return true;
	}

	private getCategoryForms(category: number) {
		return this.forms.filter((form: any) => form.category == category);
	}

	private resetDialogValues() {
		this.editFormTemplateId = 0;
		this.selectedArticle = null;
		this.newToolArticleName = "";
		this.newToolSupplierArticleName = "";
		this.selectedKernlochdurchmesser = 0;
		this.selectedPressJoinCategory = 0;
		this.selectedMinimumDistance = 0;
		this.selectedMinimumADistance = 0;
		this.selectedGroup = null;
		this.newGroupName = "";
	}

	private async openNewTemplateDialog(category: number) {
		this.resetDialogValues();
		await this.loadPressJoinGroups();
		this.selectedPressJoinCategory = category;
		this.isEdit = false;
		this.addTemplateDialog = true;
		(this.$refs.form as any).validate();
	}

	private async openEditTemplateDialog(form: any) {
		this.resetDialogValues();
		await this.loadPressJoinGroups();
		const article = await this.loadSingleArticle(form.werkzeugId);
		this.isEdit = true;
		this.selectedArticle = article;
		this.editFormTemplateId = form.formTemplateId;
		this.selectedKernlochdurchmesser = form.kernlochDurchmesser;
		this.selectedMinimumDistance = form.minimumDistance;

		this.selectedMinimumADistance = form.minimumEdgeDistance + form.kernlochDurchmesser / 2;
		this.selectedPressJoinCategory = form.category;
		this.selectedGroup = this.pressJoinGroups.find((g: any) => g.id === form.groupId);
		this.newGroupName = this.selectedGroup.name;
		this.newToolArticleName = form.werkzeugName;
		this.newToolSupplierArticleName = form.supplierArticleId;
		this.addTemplateDialog = true;
		(this.$refs.form as any).validate();
	}

	private async loadPressJoinForms() {
		try {
			this.loading = true;
			const response = await httpClient().get("Material/GetAllPressJoinForms");
			this.forms = response.data;
		} catch (err) {
			this.$store.commit("setSnackbarErrorText", "Laden der Formvorlagen fehlgeschlagen.");
			console.log(err);
		} finally {
			this.loading = false;
		}
	}

	private async loadPressJoinGroups() {
		try {
			this.loading = true;
			const response = await httpClient().get("Material/GetAllPressJoinGroups");
			this.pressJoinGroups = response.data;
		} catch (err) {
			this.$store.commit("setSnackbarErrorText", "Laden der Einpressgruppen fehlgeschlagen.");
			console.log(err);
		} finally {
			this.loading = false;
		}
	}

	private async savePressJoinForm() {
		try {
			if (this.selectedArticle == null) return;
			this.loading = true;
			let groupId = 0;
			let groupName = this.newGroupName;
			if (this.selectedGroup && this.selectedGroup.id) {
				groupId = this.selectedGroup.id;
				groupName = this.selectedGroup.name;
			}
			await httpClient().post("Material/SavePressJoinForm", {
				werkzeugId: this.selectedArticle.articleId,
				werkzeugName: this.newToolArticleName,
				supplierArticleId: this.newToolSupplierArticleName,
				kernlochDurchmesser: this.selectedKernlochdurchmesser,
				minimumEdgeDistance:
					Math.round(
						(this.selectedMinimumADistance - this.selectedKernlochdurchmesser / 2) *
							10000
					) / 10000,
				minimumDistance: this.selectedMinimumDistance,
				category: this.selectedPressJoinCategory,
				groupName: groupName,
				groupId: groupId,
				formTemplateId: this.editFormTemplateId,
			});
			this.resetDialogValues();
			this.addTemplateDialog = false;
			this.$store.commit("setSnackbarText", "Neue Formvorlage wurde gespeichert.");
			this.loadPressJoinForms();
		} catch (err) {
			this.$store.commit("setSnackbarErrorText", "Speichern der Formvorlage fehlgeschlagen.");
			console.log(err);
		} finally {
			this.loading = false;
		}
	}

	private async loadSingleArticle(articleId: number) {
		try {
			this.loading = true;
			const response = await httpClient().get(
				`Article/GetArticleById?articleId=${articleId}`
			);
			let article = response.data;
			article.text = article.articleId + " | " + article.customerArticleName;
			this.gefilterteArtikel.push(article);
			return article;
		} catch {
			console.log("Loading of single article failed.");
		} finally {
			this.loading = false;
		}
	}

	@Watch("articleFilter")
	private async loadArticle() {
		try {
			this.loading = true;
			const response = await httpClient().get(
				"Article/GetArticles?" +
					`searchTerm=${this.articleFilter}&` +
					"customerId=0&" +
					"from=0&" +
					"to=20&" +
					"onlyLocked=false&" +
					"sorting=3&" + // Sorting ist hardcoded bis jetzt (LastChangeDescending = 3)
					"includeSubarticles=false"
			);
			for (const iterator of response.data) {
				iterator.text = iterator.articleId + " | " + iterator.customerArticleName;
			}

			this.gefilterteArtikel = response.data;
		} catch {
			console.log("Load article failed.");
		} finally {
			this.loading = false;
		}
	}

	private getMaterialSelectionLink(werkzeugId: number, werkzeugName: string) {
		return `/settings/pressJoinSettings/material/${werkzeugId}/${encodeURIComponent(werkzeugName)}`;
	}
}
